import { Typography, Grid, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useHealthIntervals, useProperties, useTelemetry } from 'hooks';
import TelemetryTableTitleCell from './TableCellTitle';
import { DashboardHelpers, ReadingsHelpers } from 'utils';
import MobileSurfaceGridCell from './MobileSurfaceGridCell';
import TelemetryMobileGridCell from './TelemetryMobileGridCell';
import { Swipable } from 'components';
import { Divider, Tab, Tabs } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  toggleButtonGroup: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  toggleButton: {
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      minWidth: '33%',
    },
  },
  toggleLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    padding: '0',
    zIndex: 200,
    marginBottom: theme.spacing(3),
  },
  picker: {
    marginTop: theme.spacing(1),
  },
  toggles: {
    display: 'flex',
  },
  gradient: {
    background: 'linear-gradient( #e1dad5, #cec4bb)',
  },
  tableWrapper: {
    width: '100%',
    [theme.breakpoints.down('md')]: {
      overflowX: 'auto',
      overflowY: 'hidden',
    },
    paddingBottom: '40px',
  },
  gridBG: {
    backgroundColor: '#ded6d1',
  },
  error: {
    color: 'red',
  },
  surfaceCells: {
    paddingBottom: '10px',
  },
  swipeMenu: {
    backgroundColor: '#eeeeee',
  },
  gridHeader: {
    paddingTop: '10px',
  },
}));

const MobileStatsGrid = ({
  convertPPM,
  convertTemp,
  convertUnits,
  selectedField,
  selectedDevice,
  fieldTelemetry,
  deviceTelemetry,
  depthArrayIndex,
}) => {
  const { t } = useTranslation();
  const { activeCropId } = useProperties();
  const { getStatusForValue, getIntervalForReadingAtDepth, getIntervalsForCrop, getSuggestionForInterval } =
    useHealthIntervals();
  const { isFetching } = useTelemetry();
  const classes = useStyles();
  const [mobileReadingTab, setMobileReadingTab] = useState(0);

  if (isFetching === true) {
    return (
      <Grid
        item
        container
        direction="row"
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        justifyItems="center"
      >
        <CircularProgress />
        <Typography variant="h4" style={{ paddingLeft: 15 }}>
          {t('loading')}
        </Typography>
      </Grid>
    );
  }

  let maxDepth = 0;
  let titles = [];
  let titleCells = [<TelemetryTableTitleCell key={Math.random() * 10000} title={t('depth')} />];
  let upperTitles = [];
  let data = [];
  let subterraneanData = {};
  let airData = {};

  const getInterval = (telemetryType, depth = undefined) => {
    if (activeCropId && !depth) {
      const intervals = getIntervalsForCrop(activeCropId);
      return intervals[0];
    } else if (depth) {
      const interval = getIntervalForReadingAtDepth(telemetryType, depth);

      return interval;
    }

    return {};
  };

  const telemetry =
    selectedField?.id && (selectedDevice?.id === DashboardHelpers.StatusCodes.NoDeviceSelected || !selectedDevice?.id)
      ? fieldTelemetry[selectedField.id]
      : deviceTelemetry[selectedDevice.hw_address];

  _.forOwn(telemetry, (v) => {
    data.push(v);
  });

  if (!isFetching && data.length === 0) {
    return (
      <Grid
        item
        container
        direction="row"
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        justifyItems="center"
      >
        <Typography variant="subtitle1" style={{ paddingLeft: 15 }}>
          {t('no_data_mobile')}
        </Typography>
      </Grid>
    );
  }

  if (data.length > 0) {
    const latest = data[data.length - 1];

    _.forEach(latest.data, (v, k) => {
      _.forEach(v, (_, depth) => {
        if (depth > maxDepth) {
          maxDepth = depth;
        }
      });
      if (Object.keys(v).length > 0 && Object.keys(v).includes('0')) {
        upperTitles.push(k);
        if (airData[k] === undefined) {
          airData[k] = [];
        }
        airData[k].push(v['0'].value);
      } else {
        if (!titles.includes(k)) {
          titles.push(k);
          const convert = ReadingsHelpers.getNeedsConversion(k, convertPPM, convertTemp, convertUnits);
          const units = ReadingsHelpers.getUnitsForReading(k, convert);
          titleCells.push(<TelemetryTableTitleCell key={Math.random() * 10000} title={t(k)} type={units} />);
        }

        subterraneanData[k] = v;
      }
    });
  } else {
    titleCells = [];
  }

  const mobileCells = [];
  const convert = ReadingsHelpers.getNeedsConversion(titles[mobileReadingTab], convertPPM, convertTemp, convertUnits);
  const units = ReadingsHelpers.getUnitsForReading(titles[mobileReadingTab], convert);

  mobileCells.push(
    <Grid xs={12} container item key={Math.random() * 10000} className={classes.gridHeader}>
      <Grid xs={6} item key={Math.random() * 10000}>
        <Typography key={Math.random() * 10000} align="center" variant="subtitle2">
          {t('depth')}
        </Typography>
      </Grid>
      <Grid xs={6} item key={Math.random() * 10000}>
        <Typography key={Math.random() * 10000} align="center" variant="subtitle2">
          {units}
        </Typography>
      </Grid>
    </Grid>
  );

  for (let d = 1; d <= 3; d++) {
    const title = titles[mobileReadingTab];
    const telemetryTypeBucket = subterraneanData[title];

    if (telemetryTypeBucket !== undefined && d > 0) {
      const value = telemetryTypeBucket[`${d}`];
      const nextValue = telemetryTypeBucket[`${d + 1}`];

      const nextEmpty = nextValue === undefined;
      const thisEmpty = value === undefined;
      const thisNull = !value?.value;

      if (!thisEmpty && !thisNull) {
        mobileCells.push(
          <TelemetryMobileGridCell
            key={Math.random() * 10000}
            value={value.value}
            final={nextEmpty}
            telemetryType={title}
            getInterval={getInterval}
            getSuggestion={getSuggestionForInterval}
            getHealthStatus={getStatusForValue}
            convertPPM={convertPPM}
            convertTemp={convertTemp}
            convertUnits={convertUnits}
            depth={d}
          />
        );
      } else if (thisEmpty) {
        mobileCells.push(
          <Grid xs={12} container item key={Math.random() * 10000}>
            <Grid item xs={6} key={Math.random() * 10000}>
              <Typography key={Math.random() * 10000} align="center" variant="body1">{`${d * 6}"`}</Typography>
            </Grid>

            <Grid xs={6} item key={Math.random() * 10000}>
              <Typography key={Math.random() * 10000} align="center" className={classes.error} variant="body1">
                {t('no_data_mobile')}
              </Typography>
            </Grid>
          </Grid>
        );
      } else if (thisNull) {
        mobileCells.push(
          <Grid xs={12} container item key={Math.random() * 10000}>
            <Grid item xs={6} key={Math.random() * 10000}>
              <Typography key={Math.random() * 10000} align="center" variant="body1">{`${d * 6}"`}</Typography>
            </Grid>

            <Grid xs={6} item key={Math.random() * 10000}>
              <Typography key={Math.random() * 10000} align="center" className={classes.error} variant="body1">
                {t('error')}
              </Typography>
            </Grid>
          </Grid>
        );
      }
    }
  }

  const mobileSurfaceCells = [];
  const airDataKeys = Object.keys(airData);
  _.forEach(airDataKeys, (airDataKey) => {
    if (depthArrayIndex === 0) {
      mobileSurfaceCells.push(
        <MobileSurfaceGridCell
          key={Math.random() * 10000}
          value={airData[airDataKey]}
          surfaceDataType={airDataKey}
          convertTemp={convertTemp}
          convertPPM={convertPPM}
          convertUnits={convertUnits}
        />
      );
    }
  });

  const changeTab = (event, tab) => {
    setMobileReadingTab(tab);
  };

  return (
    <div className={classes.tableWrapper}>
      <Grid container item xs={12} className={classes.surfaceCells}>
        {mobileSurfaceCells}
      </Grid>
      <Divider />
      {
        <Swipable dataKeys={titles} onTabChange={(tab) => setMobileReadingTab(tab)} tabValue={mobileReadingTab}>
          <Grid container>
            <Grid item xs={3} className={classes.swipeMenu}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                onChange={changeTab}
                value={mobileReadingTab}
                aria-label="vertical-tabs"
                sx={{ borderRight: 1, borderColor: 'divider' }}
              >
                {titles.map((key, index) => (
                  <Tab key={index} label={t(`${key}`)}>
                    <Typography></Typography>
                    <Divider />
                  </Tab>
                ))}
              </Tabs>
            </Grid>
            <Grid container item xs={9} className={classes.gridBG}>
              {mobileCells}
            </Grid>
          </Grid>
        </Swipable>
      }
    </div>
  );
};

MobileStatsGrid.propTypes = {
  tableTitles: PropTypes.array,
  getIntervals: PropTypes.func,
  getStatusForValue: PropTypes.func,
  getSuggestionForInterval: PropTypes.func,
  activeCropId: PropTypes.string,
  convertPPM: PropTypes.bool,
  convertTemp: PropTypes.bool,
  convertUnits: PropTypes.bool,
  selectedField: PropTypes.object,
  selectedDevice: PropTypes.object,
  fieldTelemetry: PropTypes.object,
  deviceTelemetry: PropTypes.object,
  depthArrayIndex: PropTypes.number,
};

MobileStatsGrid.defaultProps = {
  tableTitles: [],
  getIntervals: () => {},
  getStatusForValue: () => {},
  getSuggestionForInterval: () => {},
  activeCropId: '',
  convertPPM: false,
  convertTemp: false,
  convertUnits: false,
  selectedField: {},
  selectedDevice: {},
  fieldTelemetry: {},
  deviceTelemetry: {},
  depthArrayIndex: 0,
};

export default MobileStatsGrid;
